import type { Contract } from "../types/contract";
import type { ContractUploadDetails } from "../types/contractUploadDetails";
import { addContract } from "./contractService";
import { clearSessionAndRedirectToLogin, isAuthenticated } from "./authService";
import { formatDisplayDate } from "../utils/displayDate";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "text/plain"
];
const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".txt"];

export type ValidateFileResult =
  | { valid: true }
  | { valid: false; error: string };

function assertAuthenticated(): void {
  if (!isAuthenticated()) {
    clearSessionAndRedirectToLogin();
    throw new Error("Not authenticated");
  }
}

export const validateFile = (file: File): ValidateFileResult => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: "File size exceeds 25 MB limit" };
  }
  const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
  const typeOk = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
  if (!typeOk) {
    return { valid: false, error: "Unsupported format. Use PDF, DOC, DOCX, or TXT" };
  }
  return { valid: true };
};

export const addContractFromUpload = (details: ContractUploadDetails): Contract => {
  assertAuthenticated();
  const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const date = formatDisplayDate(new Date().toISOString());

  const contract: Contract = {
    id,
    title: details.contractName,
    type: details.contractType || "Uploaded",
    date,
    status: "Draft",
    documentRef: null,
    fileName: details.file.name,
    reviewer: null,
    businessUnit: details.businessUnit || null,
    origination: details.contractOrigination || null
  };

  addContract(contract);
  return contract;
};
